import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { baseApi } from 'shared/api';

import {
  BaseResponse,
  MassMessageCooperative,
  MassMessageBuilding,
  MassMessageRolesGroupRes,
  MassMessageRolesGroup,
  MassMessageRole,
  RoleEntityType,
  MassMessageRecipient,
  MassMessageCreateModel,
  MassMessageSpace,
  MassMessageSendingMethods,
  ExtendedMassMessage,
  MassMessageStatusCode,
  MassMessage,
} from 'models';

import { orderBy } from 'lodash';

import { customFormat } from 'utils';
import mapIds from 'shared/utils/mapIds';
import isRolesGroup from 'utils/isRolesGroup';

function getFilteredSpaces(buildings: MassMessageBuilding[], ids: string[]) {
  let spaces: MassMessageSpace[] = [];

  for (let building of buildings) {
    for (let space of building.Spaces) {
      if (ids.includes(space.Id)) {
        spaces.push(space);
      }
    }
  }

  return spaces;
}

function pickAllSpaces(buildings: MassMessageBuilding[]) {
  return buildings.reduce((acc, next) => {
    return acc.concat(next.Spaces);
  }, [] as MassMessageSpace[]);
}

function getFilteredRoles(
  rolesGroups: (MassMessageRole | MassMessageRolesGroup)[],
  ids: string[]
) {
  let roles: MassMessageRole[] = [];

  for (let roleEntity of rolesGroups) {
    if (isRolesGroup(roleEntity)) {
      for (let role of roleEntity.Roles) {
        if (ids.includes(role.Id)) {
          roles.push(role);
        }
      }
    } else {
      if (ids.includes(roleEntity.Id)) {
        roles.push(roleEntity);
      }
    }
  }

  return roles;
}

function transformRolesGroups(
  rolesGroups: MassMessageRolesGroupRes[],
  isTalotiliCoop: boolean
): (MassMessageRole | MassMessageRolesGroup)[] {
  let entities: (MassMessageRole | MassMessageRolesGroup)[] = [];

  for (let rolesGroup of rolesGroups) {
    if (rolesGroup.UniqueName === 'owners') {
      entities.push({
        CanExpand: false,
        Order: 1,
        Name: rolesGroup.Name,
        Roles: rolesGroup.Items,
        Type: RoleEntityType.Group,
        UniqueName: rolesGroup.UniqueName,
      } as MassMessageRolesGroup);

      continue;
    }

    if (rolesGroup.UniqueName === 'residents') {
      entities.push({
        CanExpand: false,
        Name: rolesGroup.Name,
        Order: 2,
        Roles: rolesGroup.Items,
        Type: RoleEntityType.Group,
        UniqueName: rolesGroup.UniqueName,
      } as MassMessageRolesGroup);

      continue;
    }

    if (rolesGroup.UniqueName === 'board') {
      entities.push({
        CanExpand: true,
        Name: rolesGroup.Name,
        Order: 3,
        Roles: rolesGroup.Items,
        Type: RoleEntityType.Group,
        UniqueName: rolesGroup.UniqueName,
      } as MassMessageRolesGroup);

      continue;
    }

    if (rolesGroup.UniqueName === 'PMCroles') {
      rolesGroup.Items.forEach((role, idx) => {
        if (role.Name === 'Isännöitsijä') {
          if (isTalotiliCoop) return;

          entities.push({
            Type: RoleEntityType.Role,
            Name: role.Name,
            Order: 4 + idx,
            DefaultSelected: role.DefaultSelected,
            Id: role.Id,
            RelatedTo: role.RelatedTo,
          } as MassMessageRole);
        }

        if (role.Name === 'Kirjanpitäjä') {
          entities.push({
            Type: RoleEntityType.Role,
            Name: role.Name,
            Order: 4 + idx,
            DefaultSelected: role.DefaultSelected,
            Id: role.Id,
            RelatedTo: role.RelatedTo,
          } as MassMessageRole);
        }
      });

      continue;
    }
  }

  return orderBy(entities, 'Order', 'asc');
}

function getDefaultSelectedRoles(
  entities: (MassMessageRole | MassMessageRolesGroup)[],
  isTalotiliCoop: boolean
) {
  let newEntities: MassMessageRole[] = [];

  for (let entity of entities) {
    if (isRolesGroup(entity)) {
      if (entity.UniqueName === 'board') {
        newEntities.push(...entity.Roles);
      }
    } else {
      if (entity.Name === 'Isännöitsijä' && !isTalotiliCoop) {
        newEntities.push(entity);
      }

      if (entity.Name === 'Kirjanpitäjä' && isTalotiliCoop) {
        newEntities.push(entity);
      }
    }
  }

  return newEntities;
}

export type RecipientsBody = {
  coopId: string | null;
  communicationMessageId: string | null;
  distributingList: number | null;
  selectedSpaces: MassMessageSpace[];
  selectedRoles: MassMessageRole[];
  sendSms: boolean;
  sendEmail: boolean;
  sendPortalMessage: boolean;
  sendFloorApp: boolean;
};

export interface FormValues {
  description: string;
  floorAppMessageDue: Date | null;
  portalMessageDue: Date | null;
  selectedRoles: MassMessageRole[];
  selectedSpaces: MassMessageSpace[];
  sendEmail: boolean;
  sendFloorApp: boolean;
  sendPortalMessage: boolean;
  sendSMS: boolean;
  subject: string;
}

interface AdditionalValues {
  availableSendingMethods: MassMessageSendingMethods[];
  isTalotiliCoop: boolean;
}

export interface InitialFormData {
  initialValues: FormValues & AdditionalValues;
  buildings: MassMessageBuilding[];
  rolesEntities: (MassMessageRole | MassMessageRolesGroup)[];
}

export const communicationMessagesApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['Roles'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getCommunicationMessage: builder.query<
        {
          CommunicationMessage: MassMessage;
          Buildings: MassMessageBuilding[];
          RoleEntities: (MassMessageRole | MassMessageRolesGroup)[];
        },
        {
          coopId: string | null;
          messageId: string | null;
        }
      >({
        async queryFn(
          { coopId, messageId },
          _queryApi,
          _extraOptions,
          fetchWithBQ
        ) {
          const initialValuesRes = await fetchWithBQ({
            url: `/user/cooperatives/${coopId}/mass-messages/${messageId}`,
            method: 'GET',
          });

          if (initialValuesRes.error)
            return { error: initialValuesRes.error as FetchBaseQueryError };

          const communicationMessage = (
            initialValuesRes.data as BaseResponse<ExtendedMassMessage>
          ).Data;

          const getListsRes = async () => {
            return Promise.all([
              fetchWithBQ({
                url: `/cooperatives/${coopId}/mass-messages/spaces`,
                method: 'GET',
              }),
              fetchWithBQ({
                url: `/cooperatives/${coopId}/mass-messages/roles`,
                method: 'GET',
              }),
            ]);
          };

          const [buildingsListRes, rolesListRes] = await getListsRes();

          const buildings =
            (
              buildingsListRes.data as BaseResponse<MassMessageCooperative | null>
            ).Data?.Buildings ?? [];
          const rolesEntities = rolesListRes.error
            ? []
            : transformRolesGroups(
                (rolesListRes.data as BaseResponse<MassMessageRolesGroupRes[]>)
                  .Data,
                !!communicationMessage.IsTalotiliCoop
              );

          return {
            data: {
              CommunicationMessage: {
                ...communicationMessage,
                Spaces: getFilteredSpaces(
                  buildings,
                  communicationMessage.Spaces
                ),
                RecipientRoles: communicationMessage.RecipientRoles
                  ? getFilteredRoles(
                      rolesEntities,
                      communicationMessage.RecipientRoles
                    )
                  : [],
              },
              Buildings: buildings,
              RoleEntities: rolesEntities,
            },
          };
        },
      }),
      getInitialFormValue: builder.query<
        InitialFormData,
        {
          coopId: string | null;
          communicationMessageId: string | null;
          method: 'create' | 'update';
        }
      >({
        async queryFn(
          { communicationMessageId, coopId, method },
          _queryApi,
          _extraOptions,
          fetchWithBQ
        ) {
          const initialValueUrl =
            method === 'create'
              ? `/user/cooperatives/${coopId}/mass-messages/form/create`
              : `/user/cooperatives/${coopId}/mass-messages/${communicationMessageId}/form/update`;

          const initialValuesRes = await fetchWithBQ({
            url: initialValueUrl,
            method: 'GET',
          });

          if (initialValuesRes.error)
            return { error: initialValuesRes.error as FetchBaseQueryError };

          const initialValues = (
            initialValuesRes.data as BaseResponse<MassMessageCreateModel>
          ).Data;

          const [cooperativeRes, rolesListRes] = await Promise.all([
            fetchWithBQ({
              url: `/cooperatives/${coopId}/mass-messages/spaces`,
              method: 'GET',
            }),
            fetchWithBQ({
              url: `/cooperatives/${coopId}/mass-messages/roles`,
              method: 'GET',
            }),
          ]);

          if (cooperativeRes.error)
            return { error: cooperativeRes.error as FetchBaseQueryError };
          if (rolesListRes.error)
            return { error: rolesListRes.error as FetchBaseQueryError };

          const cooperative = (
            cooperativeRes.data as BaseResponse<MassMessageCooperative | null>
          ).Data;
          const buildings = cooperative?.Buildings || [];

          const rolesEntities = transformRolesGroups(
            (rolesListRes.data as BaseResponse<MassMessageRolesGroupRes[]>)
              .Data,
            initialValues.IsTalotiliCoop
          );

          return {
            data: {
              initialValues: {
                availableSendingMethods: initialValues.AvailableSendingMethods,
                description: (
                  initialValues.DescriptionPlainText ??
                  initialValues.Description ??
                  ''
                ).replace(/<br\s*[\/]?>/gi, '\n'),
                floorAppMessageDue: initialValues.FloorAppMessageDue
                  ? new Date(initialValues.FloorAppMessageDue)
                  : null,
                isTalotiliCoop: !!initialValues.IsTalotiliCoop,
                portalMessageDue: initialValues.PortalMessageDue
                  ? new Date(initialValues.PortalMessageDue)
                  : null,
                selectedRoles:
                  method === 'create'
                    ? getDefaultSelectedRoles(
                        rolesEntities,
                        initialValues.IsTalotiliCoop
                      )
                    : getFilteredRoles(
                        rolesEntities,
                        initialValues.RecipientRoles
                      ),
                selectedSpaces:
                  method === 'create'
                    ? pickAllSpaces(buildings)
                    : getFilteredSpaces(buildings, initialValues.Spaces),
                sendEmail: !!initialValues.SendEmail,
                sendFloorApp: !!initialValues.SendFloorApp,
                sendPortalMessage: !!initialValues.SendPortalMessage,
                sendSMS: !!initialValues.SendSms,
                subject: initialValues.Subject ?? '',
              },
              buildings: buildings,
              rolesEntities: rolesEntities,
            },
          };
        },
      }),
      saveFormData: builder.mutation<
        ExtendedMassMessage,
        {
          coopId: string | null;
          files: File[];
          formData: FormValues;
          variant: 'create' | 'update';
          communicationMessageId?: string | null;
        }
      >({
        async queryFn(
          { coopId, formData, files, variant, communicationMessageId },
          _queryApi,
          _extraOptions,
          fetchWithBQ
        ) {
          let body: Record<string, any> = {
            CooperativeId: coopId,
            Subject: formData.subject,
            Description: formData.description,
            SendSms: formData.sendSMS,
            SendEmail: formData.sendEmail,
            SendPortalMessage: formData.sendPortalMessage,
            SendFloorApp: formData.sendFloorApp,
            PortalMessageDue: customFormat(formData.portalMessageDue, 'server'),
            FloorAppMessageDue: customFormat(
              formData.floorAppMessageDue,
              'server'
            ),
            RecipientRoles: mapIds(formData.selectedRoles),
            Spaces: mapIds(formData.selectedSpaces),
          };

          if (variant === 'update') {
            body.Id = communicationMessageId;
          }

          const res = await fetchWithBQ({
            url: `/user/cooperatives/${coopId}/mass-messages/${variant}`,
            method: variant === 'create' ? 'POST' : 'PUT',
            body,
          });

          if (res.error) {
            return {
              error: res.error as FetchBaseQueryError,
            };
          }

          let newCommunicationMessage = (
            res.data as BaseResponse<ExtendedMassMessage>
          ).Data;

          if (files.length && newCommunicationMessage) {
            const attachmentsFormData = new FormData();

            for (const file of files) {
              attachmentsFormData.append('file', file, file.name);
            }

            await fetchWithBQ({
              url: `/user/cooperatives/${coopId}/documents/uds_communicationmessage/${newCommunicationMessage.Id}`,
              method: 'POST',
              body: attachmentsFormData,
            });
          }

          return {
            data: newCommunicationMessage,
          };
        },
      }),
      getRecipientsList: builder.query<
        MassMessageRecipient[],
        {
          recipientsBody: RecipientsBody;
        }
      >({
        query: ({ recipientsBody }) => ({
          url: `/cooperatives/${recipientsBody.coopId}/mass-messages/recipients`,
          method: 'POST',
          body: {
            CommunicationMessageId: recipientsBody.communicationMessageId,
            CooperativeId: recipientsBody.coopId,
            DistributingList: recipientsBody.distributingList,
            RecipientRolesIds: mapIds(recipientsBody.selectedRoles),
            SendEmail: recipientsBody.sendEmail,
            SendFloorApp: recipientsBody.sendFloorApp,
            SendSms: recipientsBody.sendSms,
            SendPortalMessage: recipientsBody.sendPortalMessage,
            SpacesIds: mapIds(recipientsBody.selectedSpaces),
          },
        }),
        transformResponse: (res: BaseResponse<MassMessageRecipient[]>) =>
          res.Data ?? [],
      }),
      distributeMessage: builder.mutation<
        boolean,
        { coopId: string; messageId: string }
      >({
        query: ({ coopId, messageId }) => ({
          url: `/user/cooperatives/${coopId}/mass-messages/${messageId}/distribute`,
          method: 'POST',
        }),
        transformResponse: () => true,
      }),
      getMessageStatus: builder.query<
        MassMessageStatusCode | null,
        { coopId: string | null; messageId: string | null }
      >({
        query: ({ coopId, messageId }) => ({
          url: `/user/cooperatives/${coopId}/mass-messages/${messageId}/status`,
          method: 'GET',
        }),
        transformResponse: (res: BaseResponse<MassMessageStatusCode>) =>
          res.Data ?? null,
      }),
    }),
  });
